import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { X, ArrowUp, ArrowDown } from "lucide-react";
import PdfPage, { SignatureGhost } from "./PdfPage";
import ThumbnailRail from "./ThumbnailRail";
import ViewerToolbar, { FitMode } from "./ViewerToolbar";
import type { SignaturePlacement } from "../state/documentStore";
import "./viewer.css";

interface PdfViewerProps {
  doc: PDFDocumentProxy;
  fileName: string;
  /** Rendered-size reporter so the shell's status bar can show the page count etc. */
  onCurrentPageChange?: (page: number) => void;
  /** Signature preview + placement mode, owned by the document store. */
  signature?: SignaturePlacement | null;
  /** Called with the clicked page (1-based) and top-left fractions. */
  onSignaturePlace?: (pageNumber: number, xFrac: number, yFrac: number) => void;
}

const ZOOM_MIN = 0.25;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.2;
// Render this many pages on each side of the viewport (virtualization window).
const BUFFER = 2;

const PdfViewer: React.FC<PdfViewerProps> = ({
  doc,
  fileName,
  onCurrentPageChange,
  signature,
  onSignaturePlace,
}) => {
  const total = doc.numPages;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [fitMode, setFitMode] = useState<FitMode>("width");
  const [rotation, setRotation] = useState(0);
  const [showThumbs, setShowThumbs] = useState(true);
  const [baseWidth, setBaseWidth] = useState(612); // natural page width @ scale 1
  const [baseHeight, setBaseHeight] = useState(792); // natural page height @ scale 1

  const [printing, setPrinting] = useState(false);

  // Search state
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState<number[]>([]);
  const [matchIdx, setMatchIdx] = useState(0);
  const textCache = useRef<Map<number, string>>(new Map());
  // Bumped on every runSearch call so a stale scan (superseded by a newer
  // query before it finished) can detect it's stale and drop its results
  // instead of racing the newer one.
  const searchToken = useRef(0);

  // Natural width of page 1 — used to compute fit-to-width / fit-to-page scale.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const page = await doc.getPage(1);
      if (cancelled) return;
      const vp = page.getViewport({ scale: 1 });
      setBaseWidth(vp.width);
      setBaseHeight(vp.height);
    })();
    return () => {
      cancelled = true;
    };
  }, [doc]);

  // Recompute zoom when fit mode or container size changes.
  const applyFit = useCallback(
    (mode: FitMode) => {
      const el = scrollRef.current;
      if (!el || mode === "none") return;
      const avail = el.clientWidth - 48; // page margins
      if (mode === "width") setZoom(Math.max(ZOOM_MIN, avail / baseWidth));
      if (mode === "page") {
        const availH = el.clientHeight - 48;
        setZoom(Math.max(ZOOM_MIN, Math.min(avail / baseWidth, availH / baseHeight)));
      }
    },
    [baseWidth, baseHeight],
  );

  useEffect(() => {
    if (fitMode !== "none") applyFit(fitMode);
  }, [fitMode, baseWidth, baseHeight, applyFit]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      if (fitMode !== "none") applyFit(fitMode);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fitMode, applyFit]);

  // Track the current page from scroll position.
  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const pages = el.querySelectorAll<HTMLElement>(".pdf-page");
    const mid = el.scrollTop + el.clientHeight / 2;
    let found = 1;
    pages.forEach((p) => {
      if (p.offsetTop <= mid) found = Number(p.dataset.page);
    });
    if (found !== currentPage) {
      setCurrentPage(found);
      onCurrentPageChange?.(found);
    }
  }, [currentPage, onCurrentPageChange]);

  const activePages = useMemo(() => {
    const set = new Set<number>();
    for (let p = currentPage - BUFFER; p <= currentPage + BUFFER; p++) {
      if (p >= 1 && p <= total) set.add(p);
    }
    return set;
  }, [currentPage, total]);

  const goToPage = useCallback((page: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const target = el.querySelector<HTMLElement>(`.pdf-page[data-page="${page}"]`);
    if (target) el.scrollTo({ top: target.offsetTop - 16, behavior: "smooth" });
  }, []);

  const zoomIn = useCallback(() => {
    setFitMode("none");
    setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP));
  }, []);
  const zoomOut = useCallback(() => {
    setFitMode("none");
    setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP));
  }, []);
  const rotate = useCallback(() => setRotation((r) => (r + 90) % 360), []);

  // Signature preview and click-to-place only work at 0° view rotation: the
  // stored fractions are of the unrotated page (see annotate.ts).
  const unrotated = rotation === 0;
  const placing = !!signature?.armed && unrotated;
  const ghostPage = signature?.image && unrotated ? signature.pageNumber : null;
  const ghost = useMemo<SignatureGhost | undefined>(
    () =>
      signature?.image && unrotated
        ? {
            image: signature.image,
            xFrac: signature.xFrac,
            yFrac: signature.yFrac,
            widthFrac: signature.widthFrac,
          }
        : undefined,
    [signature, unrotated],
  );
  const onPlace = useCallback(
    (pageNumber: number, xFrac: number, yFrac: number) =>
      onSignaturePlace?.(pageNumber, xFrac, yFrac),
    [onSignaturePlace],
  );

  // Ctrl + wheel zooms instead of scrolling, matching browsers/PDF readers.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      setFitMode("none");
      const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
      setZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z + delta)));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // Keyboard shortcuts.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        e.preventDefault();
        setSearchOpen(true);
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === "=" || e.key === "+")) {
        e.preventDefault();
        zoomIn();
      } else if ((e.ctrlKey || e.metaKey) && e.key === "-") {
        e.preventDefault();
        zoomOut();
      } else if ((e.ctrlKey || e.metaKey) && e.key === "0") {
        e.preventDefault();
        setFitMode("width");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomIn, zoomOut]);

  // --- Search: scan page text (cached) for the query, jump between matches. ---
  const runSearch = useCallback(
    async (q: string) => {
      const token = ++searchToken.current;
      const needle = q.trim().toLowerCase();
      if (!needle) {
        setMatches([]);
        setMatchIdx(0);
        return;
      }
      const hits: number[] = [];
      for (let p = 1; p <= total; p++) {
        let text = textCache.current.get(p);
        if (text === undefined) {
          try {
            const page = await doc.getPage(p);
            const content = await page.getTextContent();
            text = content.items
              .map((it) => ("str" in it ? it.str : ""))
              .join(" ")
              .toLowerCase();
          } catch {
            text = ""; // malformed page — skip it, don't abort the whole search
          }
          textCache.current.set(p, text);
        }
        if (text.includes(needle)) hits.push(p);
        // A newer search started while this one was scanning — stop and let
        // that one own the result instead of racing it.
        if (token !== searchToken.current) return;
      }
      setMatches(hits);
      setMatchIdx(0);
      if (hits.length) goToPage(hits[0]);
    },
    [doc, total, goToPage],
  );

  // Above this page count, rendering every page to a full-resolution PNG
  // before printing can hold hundreds of MB of image data at once — confirm
  // with the user rather than silently spiking memory (or locking up the UI)
  // on a huge document.
  const PRINT_CONFIRM_THRESHOLD = 150;

  // Print renders every page independently (not just the virtualized ones
  // currently mounted) to its own canvas at print resolution, then hands the
  // images to a hidden iframe so only the document — not the app chrome —
  // goes to the printer.
  const handlePrint = useCallback(async () => {
    if (printing) return;
    if (
      total > PRINT_CONFIRM_THRESHOLD &&
      !window.confirm(
        `This document has ${total} pages. Rendering all of them for printing may take a while and use a lot of memory. Continue?`,
      )
    ) {
      return;
    }
    setPrinting(true);
    try {
      const images: { dataUrl: string; widthIn: number; heightIn: number }[] = [];
      for (let p = 1; p <= total; p++) {
        const page = await doc.getPage(p);
        const viewport = page.getViewport({ scale: 1 });
        const printScale = 2; // ~144dpi, crisp enough for most printers
        const printViewport = page.getViewport({ scale: printScale });
        const canvas = document.createElement("canvas");
        canvas.width = Math.ceil(printViewport.width);
        canvas.height = Math.ceil(printViewport.height);
        const ctx = canvas.getContext("2d");
        if (!ctx) continue;
        await page.render({ canvasContext: ctx, viewport: printViewport }).promise;
        images.push({
          dataUrl: canvas.toDataURL("image/png"),
          widthIn: viewport.width / 72,
          heightIn: viewport.height / 72,
        });
        canvas.width = 0;
        canvas.height = 0;
      }

      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);

      const doc2 = iframe.contentDocument;
      if (doc2) {
        doc2.open();
        doc2.write(
          `<!doctype html><html><head><title>${fileName}</title><style>
            html,body{margin:0;padding:0;}
            .pg{page-break-after:always;}
            .pg:last-child{page-break-after:auto;}
            img{display:block;width:100%;height:100%;}
          </style></head><body>${images
            .map(
              (img) =>
                `<div class="pg" style="width:${img.widthIn}in;height:${img.heightIn}in;"><img src="${img.dataUrl}" /></div>`,
            )
            .join("")}</body></html>`,
        );
        doc2.close();
      }

      const cleanup = () => {
        document.body.removeChild(iframe);
      };
      iframe.contentWindow?.addEventListener("afterprint", cleanup);
      // Give images a tick to lay out before invoking the print dialog.
      setTimeout(() => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        // Fallback cleanup in case afterprint never fires (some browsers).
        setTimeout(cleanup, 60_000);
      }, 150);
    } finally {
      setPrinting(false);
    }
  }, [doc, total, fileName, printing]);

  // Keyboard shortcut for print, alongside the existing ones below.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "p") {
        e.preventDefault();
        handlePrint();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handlePrint]);

  const stepMatch = useCallback(
    (dir: 1 | -1) => {
      if (!matches.length) return;
      const next = (matchIdx + dir + matches.length) % matches.length;
      setMatchIdx(next);
      goToPage(matches[next]);
    },
    [matches, matchIdx, goToPage],
  );

  return (
    <div className="pdf-viewer">
      <ViewerToolbar
        currentPage={currentPage}
        totalPages={total}
        zoom={zoom}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onFit={setFitMode}
        onRotate={rotate}
        onGoToPage={goToPage}
        onToggleSearch={() => setSearchOpen((s) => !s)}
        onToggleThumbnails={() => setShowThumbs((s) => !s)}
        onPrint={handlePrint}
        printing={printing}
      />

      {searchOpen && (
        <div className="viewer-search">
          <input
            autoFocus
            className="viewer-search-input"
            placeholder={`Find in ${fileName}`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                if (matches.length) stepMatch(e.shiftKey ? -1 : 1);
                else runSearch(query);
              }
              if (e.key === "Escape") setSearchOpen(false);
            }}
          />
          <span className="viewer-search-count">
            {matches.length ? `${matchIdx + 1} / ${matches.length} pages` : "—"}
          </span>
          <button className="viewer-btn" title="Previous" onClick={() => stepMatch(-1)}>
            <ArrowUp size={16} />
          </button>
          <button className="viewer-btn" title="Next" onClick={() => stepMatch(1)}>
            <ArrowDown size={16} />
          </button>
          <button className="viewer-btn" title="Close" onClick={() => setSearchOpen(false)}>
            <X size={16} />
          </button>
        </div>
      )}

      <div className="pdf-viewer-body">
        {showThumbs && (
          <ThumbnailRail
            doc={doc}
            totalPages={total}
            currentPage={currentPage}
            onJump={goToPage}
          />
        )}
        <div className="pdf-scroll" ref={scrollRef} onScroll={onScroll}>
          <div className="pdf-pages">
            {Array.from({ length: total }, (_, i) => i + 1).map((n) => (
              <PdfPage
                key={n}
                doc={doc}
                pageNumber={n}
                scale={zoom}
                rotation={rotation}
                active={activePages.has(n)}
                ghost={n === ghostPage ? ghost : undefined}
                placing={placing}
                onPlace={onPlace}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PdfViewer;
